import { requestApi } from './requestApi'
import {
  GetRequestsParams,
  ApiRequest,
  Request,
  MutationRequestParams,
  CreateRequestPayload,
  InteractRequestsCountResponse,
  PaymentLinkInfoResponse,
  PaymentLinkClaimPayload,
  PaymentLinkClaimResponse,
} from './requestTypes'
import { currencyUtils } from '@/utils'

const { formatFiatCurrency } = currencyUtils

/**
 * Transform API request to domain request
 */
function transformApiRequest(apiRequest: ApiRequest): Request {
  const { amount } = apiRequest

  // Format amounts for display
  const cryptoAmount = parseFloat(amount.amount)
    .toFixed(8)
    .replace(/\.?0+$/, '')

  // Format fiat amount with proper currency symbol and formatting
  const relativeFiatAmountFormatted = formatFiatCurrency({
    fiatCode: amount.fiat_currency || 'USD',
    value: parseFloat(amount.fiat_amount || '0'),
    showDecimals: true,
  })

  return {
    ...apiRequest,
    created_at: new Date(apiRequest.created_at * 1000),
    amountFormatted: `${cryptoAmount} ${amount.currency}`,
    relativeFiatAmountFormatted,
  }
}

/**
 * Get payment requests with domain transformation
 */
async function getRequests(params: GetRequestsParams = {}): Promise<{
  requests: Request[]
  next_page_id: number | null
}> {
  const response = await requestApi.fetchPaymentRequestsHistory(params)
  const requests = response.requests.map(transformApiRequest)

  return {
    requests,
    next_page_id: response.next_page_id,
  }
}

/**
 * Create a new payment request
 * Note: We don't transform the response because the API returns an incomplete object on creation
 */
async function createRequest(payload: CreateRequestPayload): Promise<any> {
  const response = await requestApi.createInteractRequest(payload)
  // Return response directly without transformation
  return response
}

/**
 * Decline a payment request
 */
async function declineRequest(params: MutationRequestParams): Promise<void> {
  return requestApi.declineRequest(params)
}

/**
 * Cancel a payment request
 */
async function cancelRequest(params: MutationRequestParams): Promise<void> {
  return requestApi.cancelRequest(params)
}

/**
 * Accept a payment request
 */
async function acceptRequest(params: MutationRequestParams): Promise<void> {
  return requestApi.acceptRequest(params)
}

/**
 * Send reminder for a payment request
 */
async function remindRequest(params: MutationRequestParams): Promise<void> {
  return requestApi.remindRequest(params)
}

/**
 * Take back a payment link
 */
async function takeBackRequest(params: MutationRequestParams): Promise<void> {
  return requestApi.takeBackRequest(params)
}

/**
 * Get count of pending interact requests
 */
async function getInteractRequestsCount(): Promise<InteractRequestsCountResponse> {
  return requestApi.getInteractRequestsCount()
}

/**
 * Get payment link info for claim preview
 */
async function getPaymentLinkInfo(
  code: string,
): Promise<PaymentLinkInfoResponse> {
  return requestApi.getPaymentLinkInfo(code)
}

/**
 * Claim a payment link
 */
async function claimPaymentLink(
  payload: PaymentLinkClaimPayload,
): Promise<PaymentLinkClaimResponse> {
  return requestApi.claimPaymentLink(payload)
}

export const requestServices = {
  getRequests,
  createRequest,
  declineRequest,
  cancelRequest,
  acceptRequest,
  remindRequest,
  takeBackRequest,
  getInteractRequestsCount,
  getPaymentLinkInfo,
  claimPaymentLink,
}
